// ─── PHP / MySQL REST API client ──────────────────────────────────────────────
// Set REACT_APP_API_BASE in .env.local to point at your hosting's /api folder.
// Example: REACT_APP_API_BASE=https://yourdomain.com/api
const BASE = process.env.REACT_APP_API_BASE || '/api';

// ─── Admin session ────────────────────────────────────────────────────────────
// The server (api/auth.php) checks the credentials and returns a signed token
// with an expiry. It is kept in sessionStorage so a page reload keeps the login.
const TOKEN_KEY = 'vnp-admin-token';

function readStoredToken() {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    const saved = raw && JSON.parse(raw);
    return saved && saved.expires * 1000 > Date.now() ? saved.token : null;
  } catch (_) { return null; }
}

let _adminToken = readStoredToken();
export const hasAdminSession = () => !!_adminToken;

export function clearAdminToken() {
  _adminToken = null;
  try { sessionStorage.removeItem(TOKEN_KEY); } catch (_) {}
}

/** Resolves on success; throws an Error with a user-facing message otherwise. */
export async function apiLogin(user, password) {
  const res = await fetch(`${BASE}/auth.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.token) throw new Error(data.error || 'Přihlášení se nezdařilo.');
  _adminToken = data.token;
  try { sessionStorage.setItem(TOKEN_KEY, JSON.stringify(data)); } catch (_) {}
}

function authHeader() {
  return _adminToken ? { Authorization: `Bearer ${_adminToken}` } : {};
}

// ─── Base fetch helper (GET / public) ─────────────────────────────────────────
async function req(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, opts);
  if (!res.ok) throw new Error(`API ${path} → HTTP ${res.status}`);
  return res.json();
}

// ─── Field mapping helpers ────────────────────────────────────────────────────
// PHP returns properly typed data; cars need no conversion.
// Requests: DB uses snake_case for multi-word fields, JS uses camelCase.

export function dbCarToApp(row) { return row; }
export function appCarToDb(car) { return car; }

export function dbReqToApp(row) {
  const { submitted_at, admin_note, price_range, ...rest } = row;
  return { ...rest, submittedAt: submitted_at, adminNote: admin_note, priceRange: price_range };
}

export function appReqToDb(r) {
  const { submittedAt, adminNote, priceRange, ...rest } = r;
  return { ...rest, submitted_at: submittedAt, admin_note: adminNote, price_range: priceRange };
}

// ─── Cars ─────────────────────────────────────────────────────────────────────
export const apiGetCars = () =>
  req('/cars.php');

export const apiSaveCar = (car, editId) =>
  req(`/cars.php${editId ? `?id=${editId}` : ''}`, {
    method: editId ? 'PUT' : 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(car),
  });

export const apiDeleteCar = (id) =>
  req(`/cars.php?id=${id}`, { method: 'DELETE', headers: authHeader() });

export const apiPatchCarOrder = (id, sort_order) =>
  req(`/cars.php?id=${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ sort_order }),
  });

// ─── Requests ─────────────────────────────────────────────────────────────────
export const apiGetRequests = () =>
  req('/requests.php', { headers: authHeader() });

// Public – customers submit forms without a token
export const apiSaveRequest = (data) =>
  req('/requests.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const apiUpdateRequest = (id, patch) =>
  fetch(`${BASE}/requests.php?id=${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(patch),
  });

export const apiDeleteRequest = (id) =>
  fetch(`${BASE}/requests.php?id=${id}`, { method: 'DELETE', headers: authHeader() });

// ─── Wizard config ────────────────────────────────────────────────────────────
export const apiGetWizardConfig = () =>
  req('/wizard-config.php');

export const apiSaveWizardConfig = (config) =>
  fetch(`${BASE}/wizard-config.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ config }),
  });

// ─── Send email (admin reply to customer) ─────────────────────────────────────
export const apiSendEmail = (to, subject, body) =>
  fetch(`${BASE}/send-email.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ to, subject, body }),
  }).then(res => res.json());

// ─── Image upload ─────────────────────────────────────────────────────────────
export async function apiUploadImage(blob, fileName) {
  const formData = new FormData();
  formData.append('image', blob, fileName);
  const res = await fetch(`${BASE}/upload.php`, {
    method: 'POST',
    headers: authHeader(),   // no Content-Type – browser sets multipart boundary
    body: formData,
  });
  if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`);
  const data = await res.json();
  return data.url;
}
